import { Injectable } from "@angular/core";
import { StorageService } from "app/core/services/storage.service";
import { HASH } from "app/core/theme/theme.hash";
import { environment } from "environments/environment";
import { Observable, ReplaySubject } from "rxjs";
import { THEMES } from "./theme-data";
import { ThemeTypes } from "./theme-types";
import { Theme } from "./theme.interface";

@Injectable({
  providedIn: "root",
})
export class ThemeService {
  private defaultTheme: ThemeTypes = ThemeTypes.NconnectVoiceTheme;
  private host: string = window.location.host;
  private styleSheet: HTMLElement | null =
    document.head.querySelector("#theme-stylesheet");
  public themes: Theme[] = THEMES;
  public static isDevHost: boolean = environment.testToolEnable;

  private activeThemeSource: ReplaySubject<Theme> = new ReplaySubject<Theme>(1);
  public activeTheme$: Observable<Theme> =
    this.activeThemeSource.asObservable();

  constructor() {
    this.activeThemeSource.next(this.getThemeData());
    this.setFavicon();
  }

  /**
   * Change the current theme by setting the theme in the body element and store it in the StorageService
   */
  public changeTheme(theme: ThemeTypes): void {
    if (document) {
      document.body.className = theme;
    }

    StorageService.currentTheme = theme;
    this.setStyleSheet(theme);
    this.activeThemeSource.next(this.getThemeData());
  }

  /**
   * @deprecated Use activeTheme$ instead.
   * Get the current theme by checking the host
   */
  public getTheme(): ThemeTypes {
    let themeType: ThemeTypes = "" as ThemeTypes;

    // on dev themes can be changed manually in settings
    if (ThemeService.isDevHost || (window as any).e2e) {
      themeType = StorageService.currentTheme as ThemeTypes;
    }

    if (!themeType) {
      for (const theme of this.themes) {
        if (this.host.match(theme.host)) {
          themeType = theme.value;
        }
      }
    }

    return themeType || this.defaultTheme;
  }

  public getThemeData(): Theme {
    return this.currentTheme();
  }

  /**
   * Set the favicon according to the active whitelabel partner
   */
  public setFavicon(): void {
    if (document) {
      const favicon: HTMLLinkElement = document.getElementById(
        "favicon",
      ) as HTMLLinkElement;
      if (favicon) {
        favicon.href = `${this.assetPath}/logo/favicon.ico?v=1`;
      }
    }
  }

  /**
   * Set the theme stylesheet if the currently active stylesheet isn't the same
   */
  private setStyleSheet(theme: ThemeTypes): void {
    if (!this.styleSheet) {
      return;
    }
    const styleSheetHref: string | null = this.styleSheet!.getAttribute("href");
    const isCurrentTheme: boolean = styleSheetHref!.indexOf(theme) > -1;

    if (!isCurrentTheme) {
      this.styleSheet!.setAttribute("href", `${theme}.${HASH}.css`);
    }
  }

  /**
   * Returns the asset path of the currently active theme
   */
  get assetPath(): string {
    return `assets/theme-images/${this.currentTheme().assetFolder}`;
  }

  private currentTheme(): Theme {
    return this.themes.find(
      (theme: Theme) => theme.value === this.getTheme(),
    ) as Theme;
  }
}
